import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { NgIcon } from '@ng-icons/core';
import { AuthService } from '../../../core/services/auth/auth-service';
import { LocalStorageService } from '../../../core/services/localStorage/local-storage.service';
import { ClubService } from '../../../core/services/club/club.service';
import { PwaService } from '../../../core/services/pwa/pwa.service';
import { Modal } from '../../modal/modal';
import { ButtonForm } from '../../button-form/button-form';
import { filter } from 'rxjs';

export interface NavTabItem {
  label: string;
  icon: string;
  route: string;
  exact?: boolean;
}

export interface SheetMenuItem {
  label: string;
  icon: string;
  route: string;
  description?: string;
  badge?: string;
}

@Component({
  selector: 'app-bottom-nav',
  standalone: true,
  imports: [CommonModule, RouterModule, NgIcon, Modal, ButtonForm],
  templateUrl: './bottom-nav.html',
  styleUrl: './bottom-nav.css',
})
export class BottomNav implements OnInit {
  @Input() role: string = 'ADMIN';

  private router = inject(Router);
  private authService = inject(AuthService);
  protected localStorageService = inject(LocalStorageService);
  private clubService = inject(ClubService);
  protected pwaService = inject(PwaService);

  protected profile = this.authService.profile;
  protected clubs = this.clubService.clubs;
  protected activeClub = signal<any>(null);

  isMenuOpen = signal<boolean>(false);
  showLogoutModal = signal<boolean>(false);

  // Tabs displayed on the bottom bar
  navTabs = computed<NavTabItem[]>(() => {
    const currentRole = this.role || this.localStorageService.getRole() || 'ADMIN';
    if (currentRole === 'SUPER_ADMIN') {
      return [
        { label: 'Accueil', icon: 'layout-dashboard', route: '/super-admin/dashboard', exact: true },
        { label: 'Membres', icon: 'users', route: '/super-admin/member-ships' },
        { label: 'Coachs', icon: 'award', route: '/super-admin/coach-admissions' },
        { label: 'Profil', icon: 'user', route: '/super-admin/profil' },
      ];
    }
    if (currentRole === 'COACH') {
      return [
        { label: 'Accueil', icon: 'layout-dashboard', route: '/coach/dashboard', exact: true },
        { label: 'Clients', icon: 'users', route: '/coach/customers' },
        { label: 'Packs', icon: 'tag', route: '/coach/packages' },
      ];
    }
    if (currentRole === 'MANAGER') {
      return [
        { label: 'Accueil', icon: 'layout-dashboard', route: '/manager/dashboard', exact: true },
        { label: 'Clients', icon: 'users', route: '/manager/customers' },
        { label: 'Abonnements', icon: 'star', route: '/manager/abonnements' },
        { label: 'Séances', icon: 'calendar', route: '/manager/seances' },
      ];
    }
    // Default: ADMIN
    return [
      { label: 'Accueil', icon: 'layout-dashboard', route: '/admin/dashboard', exact: true },
      { label: 'Clients', icon: 'users', route: '/admin/customers' },
      { label: 'Abonnements', icon: 'star', route: '/admin/abonnements' },
      { label: 'Séances', icon: 'calendar', route: '/admin/seances' },
    ];
  });

  // Secondary items in the "Plus" bottom sheet
  sheetMenuItems = computed<SheetMenuItem[]>(() => {
    const currentRole = this.role || this.localStorageService.getRole() || 'ADMIN';
    if (currentRole === 'SUPER_ADMIN') {
      return [
        { label: 'Tableau de bord', icon: 'layout-dashboard', route: '/super-admin/dashboard', description: 'Vue globale et métriques' },
        { label: 'Demandes d\'adhésion', icon: 'users', route: '/super-admin/member-ships', description: 'Validation et blocage des clubs' },
        { label: 'Admissions Coachs', icon: 'award', route: '/super-admin/coach-admissions', description: 'Validation des coachs' },
        { label: 'Mon Profil', icon: 'user', route: '/super-admin/profil', description: 'Coordonnées & mot de passe' },
        { label: 'Paramètres', icon: 'settings', route: '/super-admin/setting', description: 'Configuration de la plateforme' },
      ];
    }
    if (currentRole === 'COACH') {
      return [
        { label: 'Tableau de bord', icon: 'layout-dashboard', route: '/coach/dashboard', description: 'Vue globale et métriques' },
        { label: 'Mes Clients', icon: 'users', route: '/coach/customers', description: 'Suivi des athlètes' },
        { label: 'Mes Packages', icon: 'tag', route: '/coach/packages', description: 'Offres et tarifs' },
      ];
    }
    if (currentRole === 'FREELANCE_COACH') {
      return [
        { label: 'Tableau de bord', icon: 'layout-dashboard', route: '/coach/dashboard', description: 'Vue globale et métriques' },
        { label: 'Mes Clients', icon: 'users', route: '/coach/customers', description: 'Suivi des athlètes' },
        { label: 'Mes Packages', icon: 'tag', route: '/coach/packages', description: 'Offres et tarifs' },
      ];
    }
    if (currentRole === 'MANAGER') {
      return [
        { label: 'Tableau de bord', icon: 'layout-dashboard', route: '/manager/dashboard', description: 'Opérations quotidiennes' },
        { label: 'Packs d\'abonnements', icon: 'tag', route: '/manager/pack-abonnement', description: 'Formules et tarifs d\'abonnements' },
        { label: 'Employés & Coachs', icon: 'briefcase', route: '/manager/employees', description: 'Gestion de l\'équipe et contrats' },
        { label: 'Équipements & Matériel', icon: 'hammer', route: '/manager/equipments', description: 'Parc matériel et suivi maintenance' },
        { label: 'Mon Profil', icon: 'user', route: '/admin/profil', description: 'Coordonnées personnelles' },
        { label: 'Paramètres du Club', icon: 'settings', route: '/admin/setting', description: 'Préférences générales' },
      ];
    }
    // Default: ADMIN
    return [
      { label: 'Packs d\'abonnements', icon: 'tag', route: '/admin/pack-abonnement', description: 'Formules et tarifs d\'abonnements' },
      { label: 'Employés & Coachs', icon: 'briefcase', route: '/admin/employees', description: 'Gestion de l\'équipe et contrats' },
      { label: 'Équipements & Matériel', icon: 'hammer', route: '/admin/equipments', description: 'Parc matériel et suivi maintenance' },
      { label: 'Mon Profil', icon: 'user', route: '/admin/profil', description: 'Coordonnées du compte et sécurité' },
      { label: 'Paramètres du Club', icon: 'settings', route: '/admin/setting', description: 'Horaires, devises et configuration' },
    ];
  });

  ngOnInit() {
    // Close sheet upon route change
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(() => {
      this.isMenuOpen.set(false);
    });

    this.initActiveClub();
  }

  private initActiveClub() {
    const clubsList = this.clubs();
    const activeId = this.localStorageService.getClubId();
    const active = clubsList.find((c: any) => c.id === activeId) || clubsList[0];
    if (active) {
      this.activeClub.set(active);
    }
  }

  selectClub(club: any) {
    this.localStorageService.saveSelectedClubId(club.id);
    this.activeClub.set(club);
    this.isMenuOpen.set(false);
    window.location.reload();
  }

  toggleMenu() {
    this.isMenuOpen.set(!this.isMenuOpen());
  }

  closeMenu() {
    this.isMenuOpen.set(false);
  }

  openLogout() {
    this.isMenuOpen.set(false);
    this.showLogoutModal.set(true);
  }

  closeLogout() {
    this.showLogoutModal.set(false);
  }

  onLogout() {
    this.showLogoutModal.set(false);
    this.authService.logout();
  }

  triggerInstall() {
    this.isMenuOpen.set(false);
    this.pwaService.openInstallPrompt();
  }
}
